/**
 * "New game" — the result card's own button, in the rail (fix D / R2): re-seek
 * the control that just finished. Same callback, same conditions as
 * `overlay/ResultOverlay`'s `canPlayAgain` (a CASUAL finished game with a known
 * control), so the two cannot offer different things.
 */
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import {selectGameOver} from '@/Store/liveGame/selectors';
import type {LiveGameState} from '@/Store/liveGameStore';

import {startNewGame, useGameActions} from '../useGameActions';

/** the play-again row belongs to a CASUAL result (ResultOverlay's own rule). */
const casual = (gamePlayMode: string): boolean =>
  gamePlayMode !== 'tournament' && gamePlayMode !== 'arena';

type NewGameInput = {
  over: boolean;
  casual: boolean;
  /** a restored game whose control never arrived cannot be re-seeked */
  hasTc: boolean;
  watching: boolean;
};

export const selectNewGame = (s: LiveGameState): NewGameInput => ({
  over: selectGameOver(s),
  casual: casual(s.game.gamePlayMode),
  hasTc: s.game.tcMs > 0,
  watching: s.watch.active || !!s.watch.replayGameId,
});

export type NewGameActionModel = {
  label: string;
  hint: string;
  disabled: boolean;
  onPress: () => void;
};

export function useNewGameActionModel(): NewGameActionModel {
  const {
    over,
    casual: isCasual,
    hasTc,
    watching,
  } = useLiveGame(useShallow(selectNewGame));
  const {newGameHint} = useGameActions();
  const disabled = watching || !over || !isCasual || !hasTc;

  return {
    // "New 3 + 2" / "Play again" is too wide for the slot; what the press
    // will actually do (fix Q: replay the bot game, or a fresh seek) is in
    // the hint, which is also the tile's accessible name
    label: 'New game',
    hint: isCasual ? newGameHint : 'Tournament games are paired for you',
    disabled,
    onPress: startNewGame,
  };
}

export default useNewGameActionModel;
